import {
  type DateFilter,
  type DateRangeFilter,
  type Filter,
  FilterId,
  FilterType,
  type FreeTextFilter,
  type ListFilter,
  type ListSelection,
  type MultiFilter,
  type RangeFilter,
  type SchoolFilter,
  type SingleValueFilter,
  type StaticListFilter,
  type TextFilter,
  type TreeFilter,
  type TreeSelection,
} from '../../../../filters/types/filters';
import { cleanGeoLabel } from '../../../../utils/clean-geo-label';
import type { ExtractionOptions } from './extractDatasetValues';

export type ParameterSelection = TreeSelection | ListSelection;

/**
 * Extracts the RCID (internal company ID) from a selection id of the form "prefix_rcid".
 */
export const extractRcidFromId = (selectionId: string): string | null => {
  const parts = selectionId.split('_');
  const rcid = parts[parts.length - 1];
  return rcid && rcid.length > 0 ? rcid : null;
};

const GEO_FILTER_IDS: ReadonlySet<string> = new Set([
  FilterId.GEOGRAPHY,
  FilterId.MULTI_GEOGRAPHY,
]);

function cleanName(name: string, filterId: string): string {
  return GEO_FILTER_IDS.has(filterId) ? cleanGeoLabel(name) : name;
}

/**
 * Formats a filter value as a string for template display.
 * Handles all filter types defined in the application.
 */
export const formatFilterValue = (filter: Filter): string => {
  switch (filter.type) {
    case FilterType.LIST:
      return (filter as ListFilter).selections
        .map((selection) => cleanName(selection.name, filter.id))
        .join(', ');

    case FilterType.STATIC_LIST:
      return (filter as StaticListFilter).selections
        .map((selection) => cleanName(selection.name, filter.id))
        .join(', ');

    case FilterType.TREE:
      return (filter as TreeFilter).selections
        .map((selection) => cleanName(selection.name, filter.id))
        .join(', ');

    case FilterType.RANGE: {
      const rangeFilter = filter as RangeFilter;
      const min = rangeFilter.min ?? '';
      const max = rangeFilter.max ?? '';
      if (min === '' && max === '') return '';
      return `${min} - ${max}`;
    }

    case FilterType.DATE_RANGE: {
      const dateRangeFilter = filter as DateRangeFilter;
      const minDate = dateRangeFilter.min?.toISOString().split('T')[0] ?? '';
      const maxDate = dateRangeFilter.max?.toISOString().split('T')[0] ?? '';
      if (!minDate && !maxDate) return '';
      return `${minDate} - ${maxDate}`;
    }

    case FilterType.DATE: {
      const dateFilter = filter as DateFilter;
      return dateFilter.value?.toISOString().split('T')[0] ?? '';
    }

    case FilterType.TEXT:
      return (filter as TextFilter).value ?? '';

    case FilterType.FREE_TEXT:
      return (filter as FreeTextFilter).text?.join(', ') ?? '';

    case FilterType.VALUE: {
      const valueFilter = filter as SingleValueFilter<unknown>;
      if (valueFilter.value === null || valueFilter.value === undefined) {
        return '';
      }
      if (typeof valueFilter.value === 'object') {
        const val = valueFilter.value;
        if ('name' in val && typeof val.name === 'string') {
          return val.name;
        }
        if ('label' in val && typeof val.label === 'string') {
          return val.label;
        }
        return JSON.stringify(val);
      }
      return String(valueFilter.value);
    }

    case FilterType.SCHOOL: {
      const schoolFilter = filter as SchoolFilter;
      const items = schoolFilter.schoolResultItems ?? {};
      return Object.values(items)
        .map((item) => item.name || item.label || item.primary_name)
        .filter(Boolean)
        .join(', ');
    }

    case FilterType.MULTI: {
      const multiFilter = filter as MultiFilter<Filter>;
      return (multiFilter.instances ?? [])
        .map((instance) => formatFilterValue(instance))
        .filter(Boolean)
        .join('; ');
    }

    default:
      return '';
  }
};

/**
 * Returns true when a parameter selection matches an entity by name or by RCID.
 * Use for box/chart highlighting when chart data uses short_name (e.g. "Amazon")
 * and the filter selection uses long name (e.g. "Amazon.com, Inc.") via RCID match.
 */
export const isParameterSelectionEqualToEntity = (
  parameter: ParameterSelection,
  entityDim: string,
  entityRcid?: string | null
): boolean => {
  if (parameter.name === entityDim) return true;
  if (entityRcid != null && entityRcid !== '') {
    const selectionRcid = extractRcidFromId(parameter.id);
    if (selectionRcid != null && selectionRcid === entityRcid) return true;
  }
  return parameter.id === entityDim;
};

/**
 * Resolves a company RCID from the COMPANY filter when the given label matches
 * the filter's display value or a selection name.
 */
export const resolveCompanyRcidFromFilters = (
  companyLabel: string,
  filters: Filter[]
): string | null => {
  const companyFilter = filters.find((f) => f.id === FilterId.COMPANY);
  if (!companyFilter) return null;

  const selections = (companyFilter as TreeFilter).selections;
  if (selections.length === 0) return null;

  const formattedFilterValue = formatFilterValue(companyFilter);
  const isFilterOutput = companyLabel === formattedFilterValue;
  const selection = isFilterOutput
    ? (selections[0] ?? null)
    : (selections.find((s) => s.name === companyLabel) ?? null);

  if (!selection) return null;

  return extractRcidFromId(selection.id);
};

export type ApplyCompanyRcidResult = {
  options: ExtractionOptions;
  companyRcid: string | null;
  originalEntityOrLabel: string;
};

/**
 * Applies company RCID resolution to extraction options: if entity/label is set,
 * resolves to RCID when possible and returns options with entity set to RCID (or original).
 */
export const applyCompanyRcidToExtractionOptions = (
  extractionOptions: ExtractionOptions,
  filters: Filter[]
): ApplyCompanyRcidResult => {
  const entityOrLabel =
    extractionOptions.entity?.trim() || extractionOptions.label?.trim() || '';
  if (!entityOrLabel) {
    return {
      options: extractionOptions,
      companyRcid: null,
      originalEntityOrLabel: '',
    };
  }
  const companyRcid = resolveCompanyRcidFromFilters(entityOrLabel, filters);
  const primaryEntityKey = companyRcid ?? entityOrLabel;
  return {
    options: {
      ...extractionOptions,
      entity: primaryEntityKey,
      label: undefined,
    },
    companyRcid,
    originalEntityOrLabel: entityOrLabel,
  };
};

/**
 * Resolves excludeEntity in extraction options to RCID when the COMPANY filter
 * provides a matching selection, so exclusion is applied by internal ID.
 */
export const resolveExcludeEntityInOptions = (
  extractionOptions: ExtractionOptions,
  filters: Filter[]
): ExtractionOptions => {
  if (extractionOptions.excludeEntity == null) return extractionOptions;
  const resolved =
    resolveCompanyRcidFromFilters(extractionOptions.excludeEntity, filters) ??
    extractionOptions.excludeEntity;
  return {
    ...extractionOptions,
    excludeEntity: resolved,
  };
};
